import { Field } from 'formik'
import React from 'react'
import { useParams } from 'react-router-dom'

import { usePlatformFeatureFlags } from '@components'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { ButtonClean, Checkbox, Fieldset, Subsection } from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { routing } from '@utils/routing'

import { useAdminModeContext } from '../../../../../../App/providers/AdminModeProvider'
import { useShopShippingFlatRatesQuery } from '../../../../../hooks/useShopShippingFlatRatesQuery'
import { getFulfillmentType } from '../../../../Product.utils'
import { useOfferQuery } from '../../../../hooks'
import type { ProductFormState } from '../../../SingleProduct.types'
import { useShippingChoiceModal } from '../shared/SelfOrAssistedShippingChoiceModal'
import { ShippingStartDate } from '../shared/ShippingStartDate/ShippingStartDate'
import { ShipmentWeightField } from '../shared/ShippingWeight/ShipmentWeightField'
import { TaxFields } from '../shared/TaxFields'
import * as S from './styled'

export const ProductShippingByCreator = () => {
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const adminMode = useAdminModeContext()
  const { openShippingModal } = useShippingChoiceModal()
  const { values } = useFormikContext<ProductFormState>()

  const { productId = '' } = useParams<'productId'>()
  const { offerQuery } = useOfferQuery([productId])

  const { isShippingTypeEditable } = offerQuery?.data?.config || {}
  const canChangeShippingType =
    isShippingTypeEditable &&
    ((hasFeatureFlag('assisted_product_shipping') &&
      hasPermission('products.thirdPartyLogistics.edit')) ||
      Boolean(adminMode?.isActive))

  const fulfillmentType = getFulfillmentType(values.fulfillmentService)

  const { shopShippingFlatRatesQuery } = useShopShippingFlatRatesQuery()
  const areFlatRatesEnabled = Boolean(shopShippingFlatRatesQuery.data?.enabled)
  const { currentShop } = useCurrentShop()
  const isShopOriginAddressDefined = Boolean(currentShop?.originAddress)

  const renderSettingsButton = () => {
    if (!hasPermission('settings.shipping')) return

    return (
      <ButtonClean label="Settings" to={routing.settings.shipping.self} appearance="secondary" />
    )
  }

  return (
    <Subsection title="Shipping">
      {fulfillmentType === 'FULFILLED_BY_CREATOR' && (
        <S.ShippingSubtitle>
          <strong>I’m shipping orders myself</strong>
          {canChangeShippingType && (
            <ButtonClean label="Change" onClick={() => openShippingModal()} />
          )}
        </S.ShippingSubtitle>
      )}

      {(() => {
        switch (true) {
          case fulfillmentType === 'FULFILLED_BY_CREATOR' && !isShopOriginAddressDefined:
            return (
              <S.CopyContainer>
                <p>
                  In order to properly calculate shipping costs for self-fulfilled products you have
                  to setup your shipping location. {renderSettingsButton()}
                </p>
              </S.CopyContainer>
            )

          case fulfillmentType === 'FULFILLED_BY_CREATOR' && !areFlatRatesEnabled:
            return (
              <S.CopyContainer>
                <p>
                  Shipping costs will be estimated based on order weight during checkout.{' '}
                  {renderSettingsButton()}
                </p>
              </S.CopyContainer>
            )

          case fulfillmentType === 'FULFILLED_BY_CREATOR' && areFlatRatesEnabled:
            return (
              <S.CopyContainer>
                <p>
                  You charge a fixed price for shipping based on the number of items in the cart.{' '}
                  {renderSettingsButton()}
                </p>
              </S.CopyContainer>
            )

          default:
            return null
        }
      })()}

      <Fieldset>
        <ShipmentWeightField />
        <ShippingStartDate />

        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Checkbox}
              name="isCustomsInformationEnabled"
              label={
                <div>
                  Set customs information for international shipments.{' '}
                  <ButtonClean
                    label="Learn more"
                    href={routing.external.helpCenter.hsCodes}
                    target="_blank"
                    appearance="secondary"
                  />
                </div>
              }
            />
          </Fieldset.Item>
        </Fieldset.Row>
        {values.isCustomsInformationEnabled && (
          <Fieldset>
            <TaxFields />
          </Fieldset>
        )}
      </Fieldset>
    </Subsection>
  )
}
